import getAuthenticatedHeaders from 'services/firebase/getAuthenticatedHeaders'

/**
 * Moves a node in the IngredientsTree via backend API.
 *
 * @param {Object} props - The properties for moving the node.
 * @param {string} props._id - The ID of the node to move (required).
 * @param {string} props._path - The current path of the node (required).
 * @param {string|null} props.targetParentPath - The path of the target parent (null for root).
 * @param {string} props.userId - The ID of the current user (required).
 * @returns {Promise<Object>} - A promise that resolves to an object with:
 *   - node: The moved node
 *   - code: Response code ('OK')
 */
const moveNode = async (props) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_FIREBASE_FUNCTIONS_API_URL}/ingredients/moveNode`,
      {
        method: 'PUT',
        headers: await getAuthenticatedHeaders({
          'Content-Type': 'application/json'
        }),
        body: JSON.stringify(props)
      }
    )

    const result = await response.json()

    // Handle error responses
    if (!response.ok || result.success === false) {
      // Build error message from available information
      let errorMessage = 'Failed to move node'
      if (result.message) {
        errorMessage = result.message
      } else if (response.statusText) {
        errorMessage = `Failed to move node: ${response.statusText}`
      }

      const error = new Error(errorMessage)
      error.code = result.code || 'UNKNOWN_ERROR'
      error.details = result.details
      error.response = {
        status: response.status,
        statusText: response.statusText,
        data: result
      }
      throw error
    }

    // Return structured response
    return {
      node: result.node,
      code: result.code || 'OK'
    }
  } catch (error) {
    console.error('moveNode API: Error moving node', {
      error: error.message,
      code: error.code,
      details: error.details,
      props
    })
    throw error
  }
}

export default moveNode
